import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { MdOutlineVisibility, MdLink } from "react-icons/md";
import ApiService from "../../api/app.api";
import Table from "./components/user_table";
import DashboardLayout from "../layouts/admin_layout";
import Breadcrumb from "../../components/bread_crumb";
import DeleteConfirmationModal from "../../components/delete_confirm";
import IconButton from "../../components/icon_button";

const api = new ApiService();

// The four request types users send from the app. This page used to list
// invitations (which have their own Invites section), so these had no admin
// screen at all.
const TABS = [
	{ key: "dua-e-shifa", label: "Dua-e-Shifa" },
	{ key: "get-well-card", label: "Get Well Cards" },
	{ key: "salatul-wahshat", label: "Salatul Wahshat" },
	{ key: "surat-al-fatiha", label: "Surah al-Fatiha" },
];

const formatDate = (value) => (value ? new Date(value).toLocaleString() : "");
const requester = (row) => {
	const sender = row.sender || row.user || {};
	const name = [sender.firstname, sender.lastname].filter(Boolean).join(" ");
	return { name: name || "-", email: sender.email || "" };
};

function RequesterCell({ row }) {
	const { name, email } = requester(row.original);
	return (
		<div>
			<p className="font-medium">{name}</p>
			<p className="text-xs text-gray-500">{email}</p>
		</div>
	);
}

function MessageModal({ row, onClose }) {
	const { name, email } = requester(row);
	const message = row.request || row.message || "";
	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
			onClick={onClose}>
			<div
				className="bg-white rounded shadow-lg p-6 w-[90%] max-w-2xl max-h-[80vh] overflow-y-auto"
				onClick={(e) => e.stopPropagation()}>
				<h2 className="text-lg font-bold mb-1">Shared message</h2>
				<p className="text-sm text-gray-500 mb-4">
					{name} {email && `· ${email}`} · {formatDate(row.createdAt)}
				</p>
				{(row.contactPhone || row.contactEmail) && (
					<p className="text-sm mb-4">
						<span className="font-medium">Contact: </span>
						{[row.contactPhone, row.contactEmail].filter(Boolean).join(" · ")}
					</p>
				)}
				<pre className="whitespace-pre-wrap font-sans text-sm leading-6">
					{message || "No message text was saved."}
				</pre>
				<div className="flex justify-end mt-6">
					<button
						className="bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded-lg"
						onClick={onClose}>
						Close
					</button>
				</div>
			</div>
		</div>
	);
}

function ShareLinkModal({ link, onClose }) {
	const [copied, setCopied] = useState(false);
	const copy = async () => {
		try {
			await navigator.clipboard.writeText(link.shareUrl);
			setCopied(true);
		} catch {
			setCopied(false);
		}
	};
	const preview = `${process.env.REACT_APP_API_URL}/api/v1/public/shares/${link.slug}`;
	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
			onClick={onClose}>
			<div className="bg-white rounded shadow-lg p-6 w-[90%] max-w-xl" onClick={(e) => e.stopPropagation()}>
				<h2 className="text-lg font-bold mb-3">Share link</h2>
				<div className="flex gap-2">
					<input readOnly value={link.shareUrl} className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm" />
					<button onClick={copy} className="px-4 py-2 rounded-lg bg-green-700 text-white text-sm">
						{copied ? "Copied" : "Copy"}
					</button>
				</div>
				<p className="text-xs text-gray-500 mt-3">
					The page this link opens is part of the next sprint (P2). Until then you can check what
					it will show here:{" "}
					<a href={preview} target="_blank" rel="noreferrer" className="text-blue-600 underline">
						public preview
					</a>
					.
				</p>
				<div className="flex justify-end mt-5">
					<button className="bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded-lg" onClick={onClose}>
						Close
					</button>
				</div>
			</div>
		</div>
	);
}

function RowActions({ row, type, onDeleted }) {
	const [viewing, setViewing] = useState(false);
	const [confirming, setConfirming] = useState(false);
	const [link, setLink] = useState(null);
	const user = useSelector((st) => st.user).user;
	const rw = row.original;
	// Cards emailed through the old flow live in another table and have no delete route.
	const canDelete = !(type === "get-well-card" && rw.delivery === "Email");

	// Emailed Get Well cards live in another table and cannot be shared by link.
	const canShare = canDelete;
	const getLink = async () => {
		const res = await api.postRequestwithBearer(
			"api/v1/shares",
			JSON.stringify({ type, id: rw.id }),
			user.token
		);
		if (res?.status) setLink(res.data);
		else alert(res?.message || "Could not create a share link.");
	};

	const handleDelete = async () => {
		const res = await api.deleteRequest(
			`api/v1/send-requests/${type}/${rw.id}`,
			user.token
		);
		setConfirming(false);
		if (res?.status) onDeleted();
		else alert(res?.message || "Could not delete this request.");
	};

	return (
		<div className="flex justify-around items-center">
			<IconButton
				icon={MdOutlineVisibility}
				label="view"
				onClick={() => setViewing(true)}
				color="text-blue-600"
			/>
			{viewing && <MessageModal row={rw} onClose={() => setViewing(false)} />}
			{canShare && (
				<IconButton icon={MdLink} label="share link" onClick={getLink} color="text-green-700" />
			)}
			{link && <ShareLinkModal link={link} onClose={() => setLink(null)} />}
			{canDelete && (
				<DeleteConfirmationModal
					isOpen={confirming}
					onClose={() => setConfirming(false)}
					onDelete={handleDelete}
					onOpen={() => setConfirming(true)}
				/>
			)}
		</div>
	);
}

function columnsFor(type, refetch) {
	const created = { Header: "Sent", accessor: (r) => formatDate(r.createdAt), id: "createdAt" };
	const by = { Header: "Requester", id: "requester", accessor: (r) => requester(r).name, Cell: RequesterCell };
	const actions = {
		Header: "Actions",
		id: "actions",
		Cell: ({ row }) => <RowActions row={row} type={type} onDeleted={refetch} />,
	};
	switch (type) {
		case "dua-e-shifa":
			return [
				{ Header: "For (sick person)", accessor: "receiver" },
				{ Header: "Signed as", accessor: "senderName" },
				by,
				created,
				actions,
			];
		case "get-well-card":
			return [
				{ Header: "Delivery", accessor: "delivery" },
				{ Header: "Recipient", accessor: (r) => r.name || "-", id: "recipient" },
				{ Header: "Message", accessor: (r) => (r.message || "").slice(0, 80), id: "message" },
				by,
				created,
				actions,
			];
		case "salatul-wahshat":
			return [
				{ Header: "Deceased", accessor: "deceased" },
				{ Header: "Janazah", accessor: (r) => [r.date, r.time, r.timezone].filter(Boolean).join(" "), id: "when" },
				{ Header: "Location", accessor: "location" },
				by,
				created,
				actions,
			];
		case "surat-al-fatiha":
			return [
				{ Header: "Deceased", accessor: "deceased" },
				{ Header: "Relation", accessor: (r) => [r.kin, r.father && `of ${r.father}`].filter(Boolean).join(" "), id: "relation" },
				{ Header: "Signed as", accessor: "senderName" },
				{ Header: "Contact email", accessor: "contactEmail" },
				by,
				created,
				actions,
			];
		default:
			return [];
	}
}

export default function UserRequests() {
	const [tab, setTab] = useState(TABS[0].key);
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const user = useSelector((st) => st.user).user;

	const fetchData = useCallback(async () => {
		setLoading(true);
		setError("");
		try {
			const q = "limit=100";
			if (tab === "get-well-card") {
				// Two sources: cards emailed via the old flow, and cards shared from the app.
				const [emailed, shared] = await Promise.all([
					api.getRequest(`api/v1/send-requests/get-well-card?${q}`, user.token),
					api.getRequest(`api/v1/send-requests/get-well-card/shares?${q}`, user.token),
				]);
				const merged = [
					...(emailed?.data?.data || []).map((r) => ({ ...r, delivery: "Email" })),
					...(shared?.data?.data || []).map((r) => ({ ...r, delivery: "Shared from app" })),
				].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
				setRows(merged);
			} else {
				const res = await api.getRequest(`api/v1/send-requests/${tab}?${q}`, user.token);
				if (!res?.status) throw new Error(res?.message || "Could not load requests.");
				setRows(res.data?.data || []);
			}
		} catch (e) {
			setRows([]);
			setError(e.message || "Could not load requests.");
		} finally {
			setLoading(false);
		}
	}, [tab, user.token]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	const columns = useMemo(() => columnsFor(tab, fetchData), [tab, fetchData]);
	const routes = [
		{ name: "Dashboard", path: "/" },
		{ name: "Requests", path: "/requests" },
	];

	return (
		<DashboardLayout>
			<div className="flex justify-between items-center mr-16 my-2">
				<Breadcrumb routes={routes} />
			</div>
			<div className="mx-8 mt-4 flex flex-wrap items-center justify-between gap-4">
				<div className="flex flex-wrap gap-2" role="tablist">
					{TABS.map((t) => (
						<button
							key={t.key}
							role="tab"
							aria-selected={tab === t.key}
							onClick={() => setTab(t.key)}
							className={`px-4 py-2 rounded-lg text-sm font-medium ${
								tab === t.key
									? "bg-green-700 text-white"
									: "bg-gray-100 text-gray-700 hover:bg-gray-200"
							}`}>
							{t.label}
						</button>
					))}
				</div>
			</div>
			<div className="mt-6 mx-8">
				{error && <p className="text-red-600 mb-4">{error}</p>}
				{loading ? (
					<p className="text-gray-500">Loading…</p>
				) : rows.length === 0 && !error ? (
					<p className="text-gray-500">
						No requests yet. They appear here once people share them from the app.
					</p>
				) : (
					<Table key={tab} columns={columns} data={rows} />
				)}
			</div>
		</DashboardLayout>
	);
}
